<script setup>
import DayCard from './DayCard.vue';
import { formatDateString, formatWeekday } from './utils';

const props = defineProps({
    week: {
        type: Array,
        required: true
    },
    activeDay: {
        type: String,
        required: true
    },
    showPastItems: {
        type: Boolean,
        default: false
    },
    showFutureItems: {
        type: Boolean,
        default: true
    }
});

const emit = defineEmits(['showDay', 'loadMoreItems', 'loadMorePastItems', 'handleStream']);

const handleShowDate = (date) => {
    emit('showDay', date);
};
const loadMoreItems = () => {
    emit('loadMoreItems');
};
const loadMorePastItems = () => {
    emit('loadMorePastItems');
};

const handleStream = (id) => {
    emit('handleStream', id);
};
</script>

<template>
    <div class="relative">
        <!-- Mobile View: Days under each other -->
        <div class="flex flex-wrap gap-small items-stretch">
            <template v-for="day in props.week" :key="day.date">
                <div class="w-full sm:w-auto flex sm:flex-col items-stretch">
                    <div class="block sm:hidden w-full">
                        <div class="grid xs:grid-cols-4">
                            <button
                                :disabled="(day?.items?.length || 0) === 0"
                                :class="[
                                    'px-4 py-2 my-0.5 rounded-lg font-semibold transition-colors duration-200 border',
                                    (day?.items?.length || 0) === 0
                                        ? 'border-blue/30 text-blue cursor-not-allowed'
                                        : props.activeDay === day.date
                                          ? 'bg-blue text-white border-blue'
                                          : 'border-blue text-blue hover:bg-blue/70 hover:text-white'
                                ]"
                                @click="handleShowDate(day.date)"
                            >
                                <a v-if="day?.items?.length > 0" :href="'#' + day.date" class="block w-full h-full">
                                    {{ formatWeekday(day.date) }}: {{ formatDateString(day.date) }} ( {{ day?.items?.length || 0 }} )
                                    <span v-if="day?.messages?.length" class="text-center">
                                        <div v-for="(message, messageIndex) in day.messages" :key="messageIndex" class="block">{{ message }}</div>
                                    </span>
                                </a>
                                <div v-else>
                                    {{ formatWeekday(day.date) }}: {{ formatDateString(day.date) }} ( {{ day?.items?.length || 0 }} )
                                    <span v-if="day?.messages?.length" class="text-center">
                                        <div v-for="(message, messageIndex) in day.messages" :key="messageIndex" class="block">{{ message }}</div>
                                    </span>
                                </div>
                            </button>
                        </div>
                    </div>
                </div></template
            >
            <div class="flex justify-between w-full sm:hidden">
                <button
                    :disabled="!showPastItems"
                    :class="[
                        'px-4 py-2 my-1 rounded-lg font-semibold transition-colors duration-200 border flex items-center justify-center gap-2',
                        showPastItems ? 'border-blue text-blue hover:bg-blue/70 hover:text-white' : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                    ]"
                    @click="loadMorePastItems"
                >
                    <i class="fi fi-rr-arrow-left mt-1"></i>
                </button>
                <button
                    :disabled="!showFutureItems"
                    @click="loadMoreItems"
                    :class="[
                        'px-4 py-2 my-1 rounded-lg font-semibold transition-colors duration-200 border flex items-center justify-center gap-2',
                        showFutureItems ? 'border-blue text-blue hover:bg-blue/70 hover:text-white' : 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                    ]"
                >
                    <i class="fi fi-rr-arrow-right mt-1"></i>
                </button>
            </div>
        </div>

        <!-- Tablet and Desktop View: Horizontal Scroll -->
        <div class="hidden sm:flex gap-2 mt-2">
            <button
                :disabled="!showPastItems"
                class="mt-4"
                :class="[
                    'px-1 rounded-lg font-semibold transition-colors duration-200 border flex items-center justify-center gap-2',
                    showPastItems ? ' text-blue hover:bg-blue/70 hover:text-white' : ' text-gray-400  cursor-not-allowed'
                ]"
                @click="loadMorePastItems"
            >
                <i class="fi fi-rr-arrow-left mt-1"></i>
            </button>

            <div class="calendarList hidden sm:flex overflow-x-scroll py-2 scrollbar-thin pb-4 whitespace-nowrap justify-between w-full">
                <template v-for="day in props.week" :key="day.date">
                    <div class="flex-shrink-0">
                        <DayCard :day="day" :active-day="props.activeDay" class="h-full px-1" @handle-stream="handleStream" />
                    </div>
                </template>
            </div>

            <button
                :disabled="!showFutureItems"
                class="mt-4"
                :class="[
                    'px-1 rounded-lg font-semibold transition-colors duration-200 border flex items-center justify-center gap-2',
                    showFutureItems ? ' text-blue hover:bg-blue/70 hover:text-white' : ' text-gray-400  cursor-not-allowed'
                ]"
                @click="loadMoreItems"
            >
                <i class="fi fi-rr-arrow-right mt-1"></i>
            </button>
        </div>
    </div>
</template>

<style>
.calendarList > .flex-shrink-0:last-child .calendarListCard .tooltip {
    --tw-translate-x: -95%;
    transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x))
        scaleY(var(--tw-scale-y));
}

.calendarList > .flex-shrink-0:first-child .calendarListCard .tooltip {
    --tw-translate-x: -10%;
    transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x))
        scaleY(var(--tw-scale-y));
}

/* Scrollbar sichtbar machen */
.scrollbar-thin {
    scrollbar-width: thin; /* Für Firefox */
}

.scrollbar-thin::-webkit-scrollbar {
    height: 8px; /* Höhe der horizontalen Scrollbar */
    display: block; /* Scrollbar immer anzeigen */
}

.scrollbar-thin::-webkit-scrollbar-thumb {
    border-radius: 4px; /* Abgerundete Ecken */
}
</style>
